import { ASSIST_FIELDS } from '@/shared/assist/AssistContract';
import type { AnalyticsField, AnalyticsMetric, TimeBucket } from '@/shared/analytics/AnalyticsTypes';

const CONFIDENCE_BEARING_FIELDS = new Set<AnalyticsField>(['difficulty', 'task_category', 'task_subcategory', 'work_related']);

/**
 * Whether a field's values are classifier-derived and carry a meaningful
 * confidence. Inherited/identity facets (department, title, manager) and base
 * columns (model, user) are not classified, so their "confidence" is not real.
 */
export function fieldCarriesConfidence(field: AnalyticsField): boolean {
    return CONFIDENCE_BEARING_FIELDS.has(field);
}

const METRIC_LABELS: Record<AnalyticsMetric, string> = {
    turns: 'Prompts',
    sessions: 'Sessions',
    users: 'Users',
    inputTokens: 'Input tokens',
    outputTokens: 'Output tokens',
    totalTokens: 'Total tokens',
    avgOutputTokens: 'Avg output',
    avgConfidence: 'Avg confidence',
    avgModelFit: 'Model fit',
    allocatedCostUsd: 'Cost',
};

export function metricLabel(metric: AnalyticsMetric): string {
    return METRIC_LABELS[metric];
}

export function formatCount(value: number): string {
    return new Intl.NumberFormat('en-US').format(Math.round(value));
}

export function formatCompact(value: number): string {
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/** Compact token counts rounded to whole units (no decimal), e.g. `1K`, `12M`. */
export function formatTokens(value: number): string {
    return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 0 }).format(value);
}

export function formatPercent(fraction: number, fractionDigits = 0): string {
    return `${(fraction * 100).toFixed(fractionDigits)}%`;
}

/**
 * Allocated USD cost for a grid cell. `0` means the group had no cost coverage
 * (the dataset's `allocated_cost` is NULL until the daily cost sync lands), so
 * it renders as an em dash rather than a misleading `$0.00`. Sub-cent but
 * nonzero amounts still surface as `<$0.01`.
 */
export function formatUsd(value: number): string {
    if (value === 0) return '—';
    if (value < 0.01) return '<$0.01';
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: value >= 100 ? 0 : 2,
    }).format(value);
}

/** A value's share of a total, in `0..1` (0 when the total is 0). */
export function share(value: number, total: number): number {
    return total === 0 ? 0 : value / total;
}

/** A value's share of the peak row as a bar width `0..100`; `floor` keeps tiny non-zero bars visible. */
export function peakShareFill(value: number, peak: number, floor = 0): number {
    return peak <= 0 ? 0 : Math.max(floor, (value / peak) * 100);
}

export function formatMetricValue(metric: AnalyticsMetric, value: number): string {
    switch (metric) {
        case 'avgConfidence':
            return formatPercent(value, 0);
        case 'avgModelFit':
            return formatCount(value);
        case 'allocatedCostUsd':
            return formatUsd(value);
        case 'avgOutputTokens':
            return formatCompact(value);
        case 'inputTokens':
        case 'outputTokens':
        case 'totalTokens':
            return formatCompact(value);
        default:
            return formatCount(value);
    }
}

export function formatTimestamp(iso: string): string {
    const time = Date.parse(iso);
    if (Number.isNaN(time)) return iso;
    return new Date(time).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/**
 * Compact label for a trend bucket string. Tolerates both the live
 * `'YYYY-MM-DD HH:MM:SS'` and fixture `'YYYY-MM-DD'` forms by reading the
 * leading date. Month buckets render as `'Jun'`, day/week as `'Jun 2'`.
 */
export function formatBucketLabel(bucket: string, timeBucket: TimeBucket): string {
    const time = Date.parse(`${bucket.slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(time)) return bucket;
    const options: Intl.DateTimeFormatOptions = timeBucket === 'month' ? { month: 'short', timeZone: 'UTC' } : { month: 'short', day: 'numeric', timeZone: 'UTC' };
    return new Date(time).toLocaleString('en-US', options);
}

export function shortUser(userId: string): string {
    const at = userId.indexOf('@');
    return at === -1 ? userId : userId.slice(0, at);
}

/**
 * A friendly display name guessed from a user id: "mina.rusk@acme.com" →
 * "Mina Rusk". Only transforms ids whose local part splits into clean
 * alphabetic name words; opaque ids fall back to {@link shortUser} verbatim.
 */
export function humanUser(userId: string): string {
    const local = shortUser(userId);
    const parts = local.split(/[._-]+/).filter((part) => part.length > 0);
    if (parts.length < 2 || !parts.every((part) => /^[a-z]+$/i.test(part))) return local;
    return parts.map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()).join(' ');
}

/**
 * A display label for a primary-field key: users are humanized, opaque
 * identifiers (`session_id`) become a friendly positional label rather than an
 * exposed hash, and everything else passes through. `index` (0-based) supplies
 * the position when ranking a list; omit it for a standalone reference.
 */
export function primaryKeyLabel(field: AnalyticsField, raw: string, index?: number): string {
    if (field === 'user_id') return humanUser(raw);
    if (field === 'session_id') return index === undefined ? 'The top session' : `Session ${index + 1}`;
    return raw;
}

const FIELD_LABELS = new Map<string, string>(ASSIST_FIELDS.map((field) => [field.value, field.label]));
const dynamicFieldLabels = new Map<string, string>();

/**
 * Registers labels for the merged field catalog (static fields + the tenant's
 * dynamic context dimensions) so {@link assistFieldLabel} can resolve a friendly
 * label for tenant-defined fields. Called once the field catalog loads.
 */
export function registerFieldLabels(defs: { value: string; label: string }[]): void {
    for (const def of defs) dynamicFieldLabels.set(def.value, def.label);
}

/** The human-readable label for an analytics field (e.g. `user_department` → "Department"), falling back to a humanized key for unknown dynamic dimensions. */
export function assistFieldLabel(field: AnalyticsField): string {
    return dynamicFieldLabels.get(field) ?? FIELD_LABELS.get(field) ?? humanizeField(field);
}

function humanizeField(field: string): string {
    const spaced = field.replace(/[_-]+/g, ' ').trim();
    return spaced.length === 0 ? field : spaced.replace(/\b\w/g, (char) => char.toUpperCase());
}

/** The display form of a filter value — humanized for the `user_id` field, verbatim otherwise. */
export function filterValueLabel(field: AnalyticsField, value: string): string {
    return field === 'user_id' ? humanUser(value) : value;
}

/**
 * LLM responses are sometimes persisted wrapped in a JSON envelope such as
 * `{"response": "...markdown..."}` — often inside a ```json fenced block, and
 * occasionally nested more than once. Surfaces only want the inner markdown,
 * never the envelope. Returns the unwrapped markdown when an envelope is present;
 * otherwise returns the text unchanged.
 */
export function unwrapResponseText(raw: string | null): string | null {
    if (raw === null) return null;
    return peelResponseEnvelope(raw) ?? raw;
}

/**
 * Recursively extracts the inner markdown from a `{"response": "..."}` JSON
 * envelope (optionally enclosed in a ```fenced``` block), returning null when the
 * text is not such an envelope so callers can fall back to the raw text. Genuine
 * content is preserved: a JSON object without a string `response` field, or a
 * legitimate fenced code block, yields null rather than being mangled.
 */
function peelResponseEnvelope(text: string): string | null {
    const candidate = stripEnclosingFence(text.trim());
    if (!candidate.startsWith('{') || !candidate.endsWith('}')) return null;
    let parsed: unknown;
    try {
        parsed = JSON.parse(candidate);
    } catch {
        return null;
    }
    if (parsed === null || typeof parsed !== 'object' || !('response' in parsed)) return null;
    const inner = (parsed as { response: unknown }).response;
    if (typeof inner !== 'string') return null;
    return peelResponseEnvelope(inner) ?? inner;
}

/**
 * If the whole string is a single fenced code block (```optional-lang … ```),
 * returns the fence's inner content; otherwise returns the string unchanged.
 */
function stripEnclosingFence(text: string): string {
    const match = /^```[^\n`]*\n([\s\S]*?)```$/.exec(text);
    if (match === null || match[1] === undefined) return text;
    return match[1].trim();
}
